import { Logo } from "./Logo";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";
import { Menu, X, Globe, Bell, User, LogOut, Loader2, KeyRound, Sun, Moon, Settings } from "lucide-react";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { SettingsModal } from "./SettingsModal";
import { clearAuthTokens, getAuthToken, getBaseUrl, setAuthTokens } from "@/lib/apiClient";
import { cn } from "@/lib/utils";

/** Chat/Inicio is product home; Taller & Herramientas stay reachable but secondary. */
const navLinks: {
  to: string;
  labelKey: string;
  exact?: boolean;
  /** Muted / de-emphasized (legacy workshop surfaces). */
  secondary?: boolean;
  primary?: boolean;
}[] = [
  { to: "/", labelKey: "nav.chat", exact: true, primary: true },
  { to: "/taller", labelKey: "nav.workshopLegacy", secondary: true },
  { to: "/herramientas", labelKey: "nav.toolsLegacy", secondary: true },
  { to: "/brain3d", labelKey: "nav.brain", secondary: true },
];

function navLinkClassName(opts: {
  isActive: boolean;
  primary?: boolean;
  secondary?: boolean;
  mobile?: boolean;
}): string {
  const { isActive, primary, secondary, mobile } = opts;
  if (mobile) {
    if (isActive && primary) {
      return "block px-3 py-2.5 rounded text-sm transition-all text-primary bg-primary/10 font-bold border border-primary/25";
    }
    if (isActive) {
      return "block px-3 py-2.5 rounded text-sm transition-all text-primary bg-primary/5 font-semibold";
    }
    if (secondary) {
      return "block px-3 py-2.5 rounded text-sm transition-all text-muted-foreground/55 hover:text-muted-foreground hover:bg-card/40";
    }
    if (primary) {
      return "block px-3 py-2.5 rounded text-sm transition-all text-foreground font-semibold hover:text-primary hover:bg-primary/5";
    }
    return "block px-3 py-2.5 rounded text-sm transition-all text-muted-foreground hover:text-foreground hover:bg-card/50";
  }

  if (isActive && primary) {
    return "px-3.5 py-1.5 rounded-lg text-xs font-extrabold transition-all bg-primary text-primary-foreground shadow-xs border border-primary";
  }
  if (isActive) {
    return "px-3.5 py-1.5 rounded-lg text-xs font-extrabold transition-all bg-card text-foreground shadow-xs border border-border/80";
  }
  if (secondary) {
    return "px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all text-muted-foreground/50 hover:text-muted-foreground hover:bg-card/40";
  }
  if (primary) {
    return "px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all text-foreground hover:text-primary hover:bg-primary/10 border border-transparent";
  }
  return "px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all text-muted-foreground hover:text-foreground hover:bg-card/50";
}

export type NavProps = {
  /**
   * `full` — marketing / taller / herramientas (default).
   * `os` — Chat home: no duplicate product nav; brand + account only.
   *         Taller/Herramientas live in the personal OS sidebar.
   */
  variant?: "full" | "os";
};

export const Nav = ({ variant = "full" }: NavProps) => {
  const location = useLocation();
  const { lang, toggleLang, t } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isOs = variant === "os";
  const [theme, setTheme] = useState<"dark" | "light">("dark");
  const apiBase = getBaseUrl().replace(/\/$/, "");

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem("prometheus_theme") || "dark";
    setTheme(savedTheme as "dark" | "light");
    if (savedTheme === "light") {
      document.documentElement.classList.add("light");
      document.documentElement.classList.remove("dark");
    } else {
      document.documentElement.classList.remove("light");
      document.documentElement.classList.add("dark");
    }
  }, []);

  const toggleTheme = () => {
    const nextTheme = theme === "dark" ? "light" : "dark";
    setTheme(nextTheme);
    localStorage.setItem("prometheus_theme", nextTheme);
    if (nextTheme === "light") {
      document.documentElement.classList.add("light");
      document.documentElement.classList.remove("dark");
    } else {
      document.documentElement.classList.remove("light");
      document.documentElement.classList.add("dark");
    }
  };

  // Auth State
  const [user, setUser] = useState<any>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Notifications State
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notifOpen, setNotifOpen] = useState(false);

  // Load User profile on mount if token exists
  const loadProfile = async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const res = await fetch(`${apiBase}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setUser(await res.json());
      } else if (res.status === 401) {
        clearAuthTokens();
      }
    } catch {}
  };

  // Load Notifications
  const loadNotifications = async () => {
    try {
      const res = await fetch(`${apiBase}/api/notifications`);
      if (res.ok) {
        setNotifications(await res.json());
      }
    } catch {}
  };

  useEffect(() => {
    loadProfile();
    loadNotifications();
    const interval = setInterval(loadNotifications, 8000);
    const handleOpenSettings = () => setSettingsOpen(true);
    window.addEventListener("open_prometheus_settings", handleOpenSettings);
    return () => {
      clearInterval(interval);
      window.removeEventListener("open_prometheus_settings", handleOpenSettings);
    };
  }, []);

  const finishLogin = async (accessToken: string, refreshToken?: string, fallbackUser?: any) => {
    setAuthTokens(accessToken, refreshToken);
    const meRes = await fetch(`${apiBase}/api/auth/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (meRes.ok) {
      setUser(await meRes.json());
    } else {
      const email = fallbackUser?.email || emailInput;
      setUser(
        fallbackUser || {
          id: "session",
          email,
          username: (email || "maker").split("@")[0],
        }
      );
    }
    setAuthOpen(false);
    setPasswordInput("");
    fetch(`${apiBase}/api/notifications/check-stock`, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  };

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError(null);
    try {
      // Same Supabase account as Aura OS: email + password only.
      const email = emailInput.trim();
      if (!email.includes("@")) {
        throw new Error("Usa el mismo correo de Aura OS (ej. tu@email.com).");
      }
      if (!passwordInput) {
        throw new Error("Escribe tu contraseña de Aura.");
      }

      // Path B: login directo a Supabase (igual que Aura) usando config pública
      let accessToken = "";
      let refreshToken: string | undefined;
      let userPayload: any = null;

      try {
        const cfgRes = await fetch(`${apiBase}/api/auth/config`);
        if (cfgRes.ok) {
          const cfg = await cfgRes.json();
          const sbRes = await fetch(
            `${String(cfg.supabase_url).replace(/\/$/, "")}/auth/v1/token?grant_type=password`,
            {
              method: "POST",
              headers: {
                apikey: cfg.anon_key,
                Authorization: `Bearer ${cfg.anon_key}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ email, password: passwordInput }),
            }
          );
          const sbBody = await sbRes.json().catch(() => ({}));
          if (sbRes.ok && sbBody.access_token) {
            accessToken = sbBody.access_token;
            refreshToken = sbBody.refresh_token;
            userPayload = {
              id: sbBody.user?.id,
              email: sbBody.user?.email || email,
              username: (sbBody.user?.email || email).split("@")[0],
            };
          } else if (!sbRes.ok) {
            const msg =
              sbBody.error_description ||
              sbBody.msg ||
              sbBody.error ||
              "Credenciales inválidas";
            // fall through to Railway proxy only on network-ish failures; credential errors stop here
            if (sbRes.status === 400 || sbRes.status === 401) {
              throw new Error(
                String(msg).toLowerCase().includes("invalid")
                  ? "Credenciales inválidas. Usa el email y contraseña exactos de Aura OS."
                  : String(msg)
              );
            }
          }
        }
      } catch (directErr: any) {
        if (
          typeof directErr?.message === "string" &&
          (directErr.message.includes("Credenciales") ||
            directErr.message.includes("invalid") ||
            directErr.message.includes("Email"))
        ) {
          throw directErr;
        }
        // continue to proxy
      }

      // Path C: proxy Railway (fallback)
      if (!accessToken) {
        const res = await fetch(`${apiBase}/api/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password: passwordInput }),
        });
        const errData = await res.json().catch(() => ({}));
        if (!res.ok) {
          const detail = errData.detail;
          const msg =
            typeof detail === "string"
              ? detail
              : Array.isArray(detail)
                ? detail.map((d: any) => d.msg || JSON.stringify(d)).join("; ")
                : "Error en la autenticación";
          throw new Error(msg);
        }
        accessToken = errData.access_token;
        refreshToken = errData.refresh_token;
        userPayload = errData.user;
      }

      if (!accessToken) {
        throw new Error("No se pudo abrir la sesión. Inténtalo de nuevo.");
      }
      await finishLogin(accessToken, refreshToken, userPayload);
    } catch (err: any) {
      setAuthError(err.message || "No se pudo iniciar sesión");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    clearAuthTokens();
    setUser(null);
  };

  const markRead = async (id: string) => {
    try {
      await fetch(`${apiBase}/api/notifications/${id}/read`, { method: "POST" });
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: "true" } : n));
    } catch {}
  };

  const unreadCount = notifications.filter((n) => n.read === "false").length;
  const makerName = String(user?.username || user?.email?.split("@")[0] || "maker");
  const makerInitial = makerName.charAt(0).toUpperCase();

  const iconBtn = cn(
    "inline-flex shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors",
    "hover:bg-muted/60 hover:text-foreground cursor-pointer",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
    isOs ? "h-8 w-8 sm:h-9 sm:w-9" : "h-9 w-9"
  );

  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-border/80 bg-card/95 backdrop-blur-md supports-[backdrop-filter]:bg-card/80 shadow-xs">
      <div
        className={
          isOs
            ? "mx-auto flex h-14 max-w-[1600px] items-center gap-2 px-3 sm:h-16 sm:gap-3 sm:px-5 lg:px-6"
            : "container flex h-16 items-center justify-between gap-3"
        }
      >
        <Link
          to="/"
          className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          aria-label="Prometheus Lab — inicio"
        >
          <Logo compact={isOs} />
        </Link>

        {/* Desktop product nav — hidden on Chat OS (personal sidebar owns navigation) */}
        {!isOs ? (
          <nav className="hidden md:flex items-center gap-1.5 p-1 rounded-xl bg-muted/50 border border-border">
            {navLinks.map((l) => {
              const isActive = l.exact
                ? location.pathname === l.to || location.pathname === ""
                : location.pathname.startsWith(l.to);
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  title={
                    l.secondary && (l.to === "/taller" || l.to === "/herramientas")
                      ? "Legacy — sigue disponible"
                      : undefined
                  }
                  className={navLinkClassName({
                    isActive,
                    primary: l.primary,
                    secondary: l.secondary,
                  })}
                >
                  {l.labelKey.includes("Brain") ? l.labelKey : t(l.labelKey)}
                </Link>
              );
            })}
          </nav>
        ) : (
          <div className="mx-auto hidden min-w-0 flex-1 items-center justify-center px-2 md:flex">
            <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-border/50 bg-muted/25 px-3 py-1">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400/90 shadow-[0_0_6px_rgba(52,211,153,0.55)]" aria-hidden />
              <p className="truncate font-mono-tech text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                Tu espacio · Engineering OS
              </p>
            </div>
          </div>
        )}

        <div
          className={cn(
            "flex shrink-0 items-center",
            isOs ? "ml-auto gap-1 sm:gap-1.5" : "gap-1.5 sm:gap-2"
          )}
        >
          {/* Notifications */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setNotifOpen(!notifOpen)}
              className={`${iconBtn} relative`}
              aria-label={unreadCount > 0 ? `Notificaciones (${unreadCount})` : "Notificaciones"}
              title="Notificaciones"
            >
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
              )}
            </button>

            {notifOpen && (
              <div className="absolute right-0 mt-2 w-[min(20rem,calc(100vw-1.5rem))] bg-card border border-border rounded-lg shadow-2xl p-3 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="flex items-center justify-between border-b border-border/40 pb-2 mb-2">
                  <span className="font-mono-tech text-[10px] text-primary uppercase tracking-widest font-bold">Alertas & Almacén</span>
                  <button
                    type="button"
                    onClick={() => fetch(`${apiBase}/api/notifications/check-stock`, { method: "POST" }).then(() => loadNotifications())}
                    className="text-[8px] font-mono-tech text-amber-400 hover:underline cursor-pointer"
                  >
                    Verificar Stock
                  </button>
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {notifications.length === 0 && (
                    <p className="text-center text-[10px] text-muted-foreground py-4 font-mono-tech">No hay notificaciones</p>
                  )}
                  {notifications.map((n) => (
                    <div
                      key={n.id}
                      onClick={() => markRead(n.id)}
                      className={`p-2 rounded text-[10px] border cursor-pointer transition-all ${
                        n.read === "false"
                          ? "bg-primary/5 border-primary/20 hover:bg-primary/10"
                          : "bg-void/40 border-border/20 opacity-60"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="font-bold text-foreground">{n.title}</span>
                        {n.read === "false" && <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />}
                      </div>
                      <p className="text-muted-foreground leading-relaxed">{n.message}</p>
                      <span className="text-[7px] text-muted-foreground/60 font-mono-tech mt-1 block">{n.created_at.slice(11, 19)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Lang / theme / settings — icon rail; lang label from md */}
          <div className="flex items-center gap-0.5 rounded-xl border border-border/40 bg-muted/20 p-0.5">
            <button
              type="button"
              onClick={toggleLang}
              className={cn(iconBtn, "w-auto gap-1 px-1.5 sm:px-2")}
              title={lang === "es" ? "Switch to English" : "Cambiar a Español"}
              aria-label={lang === "es" ? "Switch to English" : "Cambiar a Español"}
            >
              <Globe className="h-3.5 w-3.5 shrink-0" />
              <span className="hidden font-mono-tech text-[10px] uppercase tracking-wider md:inline">
                {lang === "es" ? "EN" : "ES"}
              </span>
            </button>

            <button
              type="button"
              onClick={toggleTheme}
              className={iconBtn}
              title={theme === "dark" ? "Cambiar a Modo Claro" : "Cambiar a Modo Oscuro"}
              aria-label={theme === "dark" ? "Cambiar a Modo Claro" : "Cambiar a Modo Oscuro"}
            >
              {theme === "dark" ? (
                <Sun className="h-3.5 w-3.5 text-amber-400" />
              ) : (
                <Moon className="h-3.5 w-3.5 text-primary" />
              )}
            </button>

            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              className={iconBtn}
              title="Ajustes & Configuración del Sistema"
              aria-label="Ajustes"
            >
              <Settings className="h-3.5 w-3.5 text-primary" />
            </button>
          </div>

          {/* Account — avatar chip; name from sm; logout from sm (avatar title covers session on phone) */}
          {user ? (
            <div className="flex items-center gap-0.5 sm:gap-1.5">
              <div
                className="flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/5 p-0.5 sm:pr-2.5"
                title={`Maker · ${makerName}`}
              >
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 font-mono-tech text-[11px] font-bold text-primary"
                  aria-hidden
                >
                  {makerInitial}
                </span>
                <span className="hidden max-w-[7.5rem] truncate font-mono-tech text-[10px] font-semibold uppercase tracking-wide text-primary sm:inline md:max-w-[10rem]">
                  {makerName}
                </span>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className={cn(iconBtn, "text-muted-foreground hover:text-red-400")}
                title="Cerrar sesión"
                aria-label="Cerrar sesión"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <Button
              variant="ember"
              size="sm"
              onClick={() => setAuthOpen(true)}
              className="hidden h-9 sm:inline-flex"
            >
              {t("nav.join")}
            </Button>
          )}

          {/* Mobile Hamburger — full nav only; OS uses personal sidebar */}
          {!isOs ? (
            <button
              type="button"
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`${iconBtn} md:hidden`}
              aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          ) : null}

          {!isOs && !user ? (
            <button
              type="button"
              onClick={() => setAuthOpen(true)}
              className={`${iconBtn} sm:hidden`}
              aria-label={t("nav.join")}
              title={t("nav.join")}
            >
              <User className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>

      {/* Mobile Dropdown */}
      {mobileOpen && !isOs && (
        <div className="md:hidden border-t border-border/30 bg-background/95 backdrop-blur-xl animate-in slide-in-from-top duration-200">
          <div className="container py-4 space-y-1">
            {navLinks.map((l) => {
              const isActive = l.exact
                ? location.pathname === l.to || location.pathname === ""
                : location.pathname.startsWith(l.to);
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setMobileOpen(false)}
                  className={navLinkClassName({
                    isActive,
                    primary: l.primary,
                    secondary: l.secondary,
                    mobile: true,
                  })}
                >
                  {l.labelKey.includes("Brain") ? l.labelKey : t(l.labelKey)}
                  {l.secondary && (l.to === "/taller" || l.to === "/herramientas") ? (
                    <span className="ml-2 text-[10px] uppercase tracking-wider opacity-60">
                      legacy
                    </span>
                  ) : null}
                </Link>
              );
            })}
            <button
              onClick={() => { setMobileOpen(false); setSettingsOpen(true); }}
              className="w-full text-left flex items-center gap-2 px-3 py-2.5 rounded text-sm text-muted-foreground hover:text-foreground hover:bg-card/50 font-mono-tech"
            >
              <Settings className="w-4 h-4 text-primary" />
              <span>Ajustes & Configuración</span>
            </button>
            {!user && (
              <button
                onClick={() => { setMobileOpen(false); setAuthOpen(true); }}
                className="w-full text-left block px-3 py-2.5 rounded text-sm text-primary hover:bg-primary/5 font-semibold"
              >
                {t("nav.join")}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Settings Modal */}
      <SettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />

      {/* Auth Dialog Backdrop & Modal */}
      {authOpen && createPortal(
        <div className="fixed inset-0 bg-void/85 backdrop-blur-md flex items-center justify-center z-[100] animate-in fade-in duration-200">
          <div className="bg-card border border-primary/20 rounded-lg w-80 sm:w-96 p-6 relative shadow-2xl animate-in zoom-in-95 duration-200">
            <button 
              onClick={() => setAuthOpen(false)} 
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
            
            <div className="flex items-center gap-2 border-b border-border/40 pb-3 mb-4">
              <KeyRound className="w-5 h-5 text-primary" />
              <h4 className="font-serif-display text-lg text-foreground uppercase tracking-wide">
                Acceder al Lab
              </h4>
            </div>

            <form onSubmit={handleAuthSubmit} className="space-y-4">
              {authError && (
                <div className="p-2.5 bg-red-500/10 border border-red-500/30 text-red-400 text-[10px] rounded leading-relaxed">
                  {authError}
                </div>
              )}

              <p className="text-[10px] text-muted-foreground leading-relaxed">
                Misma cuenta que Aura OS: correo y contraseña de Supabase.
              </p>

              <div>
                <label className="font-mono-tech text-[8px] text-muted-foreground uppercase block mb-1">
                  Correo (Aura)
                </label>
                <input
                  type="email"
                  autoComplete="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="tu@email.com"
                  className="w-full bg-void border border-border text-xs px-3 py-2 rounded focus:outline-none focus:border-primary text-foreground font-mono-tech"
                  required
                />
              </div>

              <div>
                <label className="font-mono-tech text-[8px] text-muted-foreground uppercase block mb-1">Contraseña</label>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  className="w-full bg-void border border-border text-xs px-3 py-2 rounded focus:outline-none focus:border-primary text-foreground font-mono-tech"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={authLoading}
                className="w-full bg-primary hover:bg-primary/95 text-primary-foreground text-xs font-mono-tech uppercase font-bold py-2 rounded cursor-pointer transition-all flex items-center justify-center gap-1.5"
              >
                {authLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                Iniciar Sesión
              </button>
            </form>

            <p className="mt-4 pt-3 border-t border-border/40 text-center text-[9px] text-muted-foreground">
              ¿Sin cuenta? Créala en Aura OS y entra aquí con el mismo correo.
            </p>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
};